import {IconButton, Tooltip} from 'platform/components';
import {ThemeIconKey} from 'platform/foundation';

import {memo} from 'react';

import {useActionLocksForRow} from '../../../../../../store/selectors/useActionLocks';
import {ActionDefinitionObject} from '../../../../../../types/ActionDefinition';
import {RowData} from '../../../../../../types/RowData';
import {getActionButtonSize} from '../../../../../../utils/getActionButtonSize';
import {getRowPaddingClass, LineHeight} from '../../../../../../utils/lineHeightToValue';
import {partitionRowActions} from '../../../../../../utils/partitionRowActions';
import {ActionColumnCell} from './components/ActionColumnCell/ActionColumnCell';
import {RowActionsOverflow} from './components/RowActionsOverflow/RowActionsOverflow';

export type RowActionsProps = {
  row: RowData;
  actionsDefinition: ActionDefinitionObject;
  onActionClick: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  isMobile: boolean;
  rowHeight: LineHeight;
  actionColumnWidth: number;
  heightPx: number;
  isSelected?: boolean;
  isStriped?: boolean;
  isDisabled?: boolean;
};

// memo: rendered N times (one per visible row); only re-renders when own row's actions change.
export const RowActions = memo((props: RowActionsProps) => {
  const {iconActions, overflowActions} = partitionRowActions(
    props.row.actions,
    props.actionsDefinition
  );
  const buttonSize = getActionButtonSize(props.rowHeight, props.isMobile);
  const paddingClass = getRowPaddingClass[props.rowHeight];
  const lockedActionKeys = useActionLocksForRow(props.row.id);

  // Plain function — React Compiler handles memoization inside memo(). No manual useCallback
  // needed: RowActionsOverflow is not memo'd itself; parent memo() guards re-renders.
  const handleOverflowActionClick = (actionKey: string, nativeEvent: MouseEvent) => {
    props.onActionClick(actionKey, props.row, {
      stopPropagation: () => undefined,
      nativeEvent,
    } as React.MouseEvent);
  };

  const hasActions = iconActions.length > 0 || overflowActions.length > 0;

  return (
    <ActionColumnCell width={props.actionColumnWidth} heightPx={props.heightPx}>
      {hasActions && (
        <div className={`flex items-center h-full gap-1 ${paddingClass}`}>
          {iconActions.map((action) => {
            const iconValue = action.definition.icon.value as ThemeIconKey;
            const isThisActionDisabled =
              props.isDisabled === true || lockedActionKeys.has(action.actionKey);

            return (
              <Tooltip
                key={action.actionKey}
                label={action.definition.title}
                description={action.definition.description}
                placement="left"
                hasAutoWidth
              >
                <IconButton
                  size={buttonSize}
                  icon={iconValue}
                  aria-label={action.definition.title ?? 'row action'}
                  onClick={(event: React.MouseEvent) =>
                    props.onActionClick(action.actionKey, props.row, event)
                  }
                  isDisabled={isThisActionDisabled}
                  data-testid={`button-rowAction.${action.actionKey}`}
                />
              </Tooltip>
            );
          })}
          <RowActionsOverflow
            overflowActions={overflowActions}
            onActionClick={handleOverflowActionClick}
            rowId={props.row.id}
            buttonSize={buttonSize}
            isDisabled={props.isDisabled === true}
            lockedActionKeys={lockedActionKeys}
          />
        </div>
      )}
    </ActionColumnCell>
  );
});
